import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const candidateSchema = z.object({
  line_item_id: z.string().uuid(),
  version: z.number().int().positive(),
  date: z.string(),
  payee: z.string(),
  amount: z.string(),
  reason_kind: z.enum(['event_window', 'merchant_match']),
  reason: z.string(),
  status: z.enum(['pending', 'dismissed', 'promoted']),
}).strict();

const candidatesSchema = z.object({ items: z.array(candidateSchema), next_cursor: z.string().nullable() }).strict();
const actionSchema = z.object({ status: z.enum(['dismissed', 'promoted']), line_item_version: z.number().int().positive().nullable(), business_line_item_id: z.string().uuid().nullable(), attestation_id: z.string().uuid().nullable() }).strict();

export type PromotionCandidate = z.infer<typeof candidateSchema>;
export type PromotionActionInput = { action: 'promote' | 'dismiss'; lineItemId: string; expectedVersion: number; businessPercent?: string; businessAmount?: string; attestation?: { statementText: string; statementVersion: string; facts: Record<string, unknown> } };

export function usePromotionCandidates(taxYear: number | undefined) {
  return useQuery({
    queryKey: ['business-promotion-candidates', taxYear ?? null],
    enabled: taxYear != null,
    queryFn: async () => {
      const response = await client.get(`/api/business/year/${taxYear}/promotion-candidates`);
      return parseEnvelope(candidatesSchema, response.data).data;
    },
  });
}

export function usePromotionCandidateAction(taxYear: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PromotionActionInput) => {
      const response = await client.post(`/api/business/year/${taxYear}/promotion-candidates/${input.lineItemId}/${input.action}`, {
        expected_version: input.expectedVersion,
        ...(input.action === 'promote' ? {
          business_percent: input.businessPercent,
          business_amount: input.businessAmount,
          attestation: input.attestation ? { statement_text: input.attestation.statementText, statement_version: input.attestation.statementVersion, facts: input.attestation.facts } : undefined,
        } : {}),
      });
      return parseEnvelope(actionSchema, response.data).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-promotion-candidates', taxYear] });
      void queryClient.invalidateQueries({ queryKey: ['business-accountant-work', taxYear] });
    },
    retry: false,
  });
}

